import React, { useContext, useEffect } from 'react'
import { useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import swal from 'sweetalert2'
import { DateTime } from 'luxon'
import { useSetState } from '@campaignhub/react-hooks'
import PageContext from '@contexts/pageContext'

import { DashboardModule, LoadingModule } from '@campaignhub/suit-theme'

import useReduxAction from '@hooks/useReduxAction'
import useSlot from '@hooks/useSlot'

import Blockouts from '@components/Blockouts'
import Scheduler from '@components/Scheduler'

const defaultState = {
  currentSlots: {},
  dateStart: DateTime.now().startOf('day').toJSDate(),
  dateEnd: DateTime.now().plus({ months: 1 }).endOf('day').toJSDate(),
  defaultCount: 20,
  loadNext: false,
  page: 0,
}

const PageContent = (props) => {
  const { slots } = props

  const [state, setState] = useSetState(defaultState)
  const {
    currentSlots, dateStart, dateEnd, defaultCount, loadNext, page,
  } = state

  const { currentUserV4, currentUserV4Loading, loadAction } = useContext(PageContext)

  const entities = useSelector(reduxState => reduxState.entities)
  const { blockouts } = entities

  const blockoutEntityReducer = useSelector(reduxState => reduxState.blockouts)
  const { result: { hasMore }, loading: blockoutsLoading } = blockoutEntityReducer

  useEffect(() => {
    setState({ currentSlots: slots })
  }, [slots, currentUserV4])

  const startDateTime = DateTime.fromJSDate(dateStart).setZone('utc', { keepLocalTime: true }).toISO()
  const endDateTime = DateTime.fromJSDate(dateEnd).setZone('utc', { keepLocalTime: true }).toISO()

  useReduxAction('blockouts', loadAction, {
    offset: page * defaultCount,
    size: defaultCount,
    sort: 'finish desc',
    startDateTime,
    endDateTime,
    loadNext,
  }, [loadAction, startDateTime, endDateTime, page])

  const updateDateRange = (dates) => {
    setState({ ...dates, page: 0, loadNext: false })
  }

  const loadMoreBlockouts = () => {
    setState({ page: page + 1, loadNext: true })
  }

  const { callbacks: { updateSlots: updateFn } } = useSlot()

  const updateSlots = (updatedSlots) => {
    setState({ currentSlots: updatedSlots })
  }

  function saveSlotUpdates(currentHours){
    if (currentUserV4.provider.minimum_slots > currentHours){
      swal.fire({
        title: 'Required Hours not Met',
        text: 'Your weekly hours are lower than the Minimum Hours Required. Please add more slots to proceed.',
        icon: 'warning',
        confirmButtonText: 'OK',
        confirmButtonColor: '#e2001a',
      })
      return null
    }

    updateFn(currentSlots).then(({ success, errors: updateErrors }) => {
      if (!success && updateErrors){
        swal.fire({
          icon: 'warning',
          title: 'Unexpected error occurred',
          html: '<p>Please screenshot the error message below and send to support@contenthouse.io so that the team can resolve the issue.</p>'
            + `<pre style="background-color:#ececec;margin-top:5px;font-size:14px">${updateErrors[0]}</pre>`,
          confirmButtonText: 'OK',
          confirmButtonColor: '#e2001a',
        })
      }
    })

    swal.fire({
      icon: 'success',
      title: 'Successfully saved',
      showConfirmButton: false,
      timer: 1500,
    }).then(() => {
      currentUserV4.provider.slots = currentSlots
    })

    return currentHours
  }

  return (
    <>
      <DashboardModule title="Work Hours">
        <LoadingModule loading={currentUserV4Loading} boxProps={{ border: 0 }} />
        {currentUserV4 && (
          <Scheduler
            slots={slots}
            callbacks={{ updateSlots, saveSlotUpdates }}
            showSave
            minimum_slots={currentUserV4.provider.minimum_slots}
            boxProps={{ padding: 'large' }}
          />
        )}
      </DashboardModule>

      <DashboardModule title="Upcoming Blockouts">
        {currentUserV4 && (
          <Blockouts
            blockouts={Object.values(blockouts).filter(x => x.user === `User/${currentUserV4.id}` || x.user === currentUserV4.id)}
            callbacks={{ setDateRange: updateDateRange, loadMore: loadMoreBlockouts }}
            dateStart={dateStart}
            dateEnd={dateEnd}
            hasMore={hasMore}
            loading={blockoutsLoading}
          />
        )}
      </DashboardModule>
    </>
  )
}

PageContent.propTypes = {
  slots: PropTypes.object.isRequired,
}

export default PageContent
